import React, { createContext, useCallback, useContext, useState } from 'react';

type Size = {
  id: number;
  name: string;
  numOfSlices: number;
  numOfFlavors: number;
};

type ComplementItem = {
  id: number | string;
  name?: string;
  order: number;
  rawMaterialId?: number;
  maximumQuantity: number;
  minimumQuantity: number;
  image?: File | string;
  price: number;
  pauseSale?: boolean;
};

type Complement = {
  id?: number | string;
  name?: string;
  order?: number;
  maximumQuantity?: number;
  minimumQuantity?: number;
  isRequired?: boolean;
  items?: ComplementItem[];
  pauseSale?: boolean;
};

type Group = {
  id?: number;
  name?: string;
  image?: File | string;
  sizes?: Size[];
  complements?: Complement[];
};

type PizzaGroupContextData = Group & {
  currentTab: 0 | 1 | 2;
  isDrawerOpened?: boolean;
  complementsDrawerTab: 0 | 1;
  isComplementsDrawerOpened?: boolean;
  handleOpenDrawer(): void;
  handleCloseDrawer(): void;
  handleResetGroup(): void;
  handleChangeTab(tab: number): void;
  handleOpenComplementsDrawer(): void;
  handleCloseComplementsDrawer(): void;
  handleChangeGroup(group: Partial<Group>): void;
  handleAddComplement(complement: Complement): void;
  handleEditComplement(complement: Complement): void;
  handleChangeComplementsDrawerTab(tab: 0 | 1): void;
  handleDeleteComplement(complement: Complement): void;
};

const PizzaGroupContext = createContext<PizzaGroupContextData>(
  {} as PizzaGroupContextData,
);

const PizzaGroupProvider: React.FC = ({ children }) => {
  const [group, setGroup] = useState<Group>();
  const [currentTab, setCurrentTab] = useState<0 | 1 | 2>(0);
  const [isDrawerOpened, setIsDrawerOpened] = useState<boolean>();
  const [complementsDrawerTab, setComplementsDrawerTab] = useState<0 | 1>(0);
  const [isComplementsDrawerOpened, setIsComplementsDrawerOpened] = useState<
    boolean | undefined
  >(undefined);

  const handleOpenDrawer = useCallback(() => {
    setIsDrawerOpened(true);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setIsDrawerOpened(false);
  }, []);

  const handleOpenComplementsDrawer = useCallback(() => {
    setIsComplementsDrawerOpened(true);
  }, []);

  const handleCloseComplementsDrawer = useCallback(() => {
    setIsComplementsDrawerOpened(false);
  }, []);

  const handleChangeGroup = useCallback((partialGroup: Partial<Group>) => {
    setGroup((oldState) => ({ ...oldState, ...partialGroup }));
  }, []);

  const handleAddComplement = useCallback((complement: Complement) => {
    setGroup((oldState) => ({
      ...oldState,
      complements: [...(oldState?.complements || []), complement],
    }));
  }, []);

  const handleDeleteComplement = useCallback((complement: Complement) => {
    setGroup((oldState) => ({
      ...oldState,
      complements: (oldState?.complements || []).filter(
        (oldComplement) => oldComplement.id !== complement.id,
      ),
    }));
  }, []);

  const handleEditComplement = useCallback((complement: Complement) => {
    setGroup((oldState) => ({
      ...oldState,
      complements: (oldState?.complements || []).map((oldComplement) => {
        if (oldComplement.id === complement.id) {
          return complement;
        }

        return oldComplement;
      }),
    }));
  }, []);

  const handleChangeTab = useCallback((tab: 0 | 1 | 2) => {
    setCurrentTab(tab);
  }, []);

  const handleResetGroup = useCallback(() => {
    setGroup(undefined);
  }, []);

  const handleChangeComplementsDrawerTab = useCallback((tab: 0 | 1) => {
    setComplementsDrawerTab(tab);
  }, []);

  return (
    <PizzaGroupContext.Provider
      value={{
        currentTab,
        isDrawerOpened,
        complementsDrawerTab,
        isComplementsDrawerOpened,
        handleChangeTab,
        handleResetGroup,
        handleOpenDrawer,
        handleChangeGroup,
        handleCloseDrawer,
        handleAddComplement,
        handleEditComplement,
        handleDeleteComplement,
        handleOpenComplementsDrawer,
        handleCloseComplementsDrawer,
        handleChangeComplementsDrawerTab,
        ...group,
      }}
    >
      {children}
    </PizzaGroupContext.Provider>
  );
};

function usePizzaGroup(): PizzaGroupContextData {
  const context = useContext(PizzaGroupContext);

  if (!context)
    throw new Error('usePizzaGroup must be used within PizzaGroupProvider');

  return context;
}

export { PizzaGroupProvider, usePizzaGroup };
